import { useEffect, useId, useLayoutEffect, useRef, useState, type MouseEvent } from "react";
import { createPortal } from "react-dom";
import { Menu, X } from "lucide-react";
import { siteDesignAttributes, type LogoSize, type SiteDesign } from "../../themes";
import type { EditorPage } from "../editor.types";
import "./SiteNavigation.css";

const siteNameClass =
  "site-heading min-w-0 flex-1 heading-size-20 leading-[1.4] text-site-text wrap-anywhere hover:text-site-accent focus-visible:outline-2 focus-visible:outline-site-accent focus-visible:outline-offset-4";
/** Three curated logo heights, a size down on phones; wide logos are capped. */
const logoHeight: Record<LogoSize, string> = {
  small: "h-7 max-md:h-6 @max-[767px]/editor-preview:h-6",
  medium: "h-9 max-md:h-7 @max-[767px]/editor-preview:h-7",
  large: "h-12 max-md:h-9 @max-[767px]/editor-preview:h-9",
};

/** The logo (with or without the name), else the name. */
function Brand({ design, name, logoUrl }: { design: SiteDesign; name: string; logoUrl?: string | null }) {
  if (!logoUrl) return <>{name}</>;
  const logo = (
    <img
      src={logoUrl}
      alt={design.logoDisplay === "both" ? "" : name}
      draggable={false}
      className={`block w-auto max-w-60 shrink-0 object-contain object-left max-md:max-w-44 @max-[767px]/editor-preview:max-w-44 ${logoHeight[design.logoSize]}`}
    />
  );
  if (design.logoDisplay !== "both") return logo;
  return (
    <span className="flex items-center gap-3">
      {logo}
      <span className="min-w-0">{name}</span>
    </span>
  );
}

export function SiteNavigation({
  design,
  name,
  pages,
  currentPageId,
  href,
  onNavigate,
  logoUrl,
}: {
  /** Carried onto the menu, which is portalled outside the themed page. */
  design: SiteDesign;
  name: string;
  /** The uploaded logo's address, when the design has one. */
  logoUrl?: string | null;
  pages: EditorPage[];
  currentPageId?: string;
  href: (page: EditorPage) => string;
  onNavigate: (page: EditorPage) => void;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const toggle = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const home = pages.find((page) => page.is_home) ?? pages[0];
  useEffect(() => {
    if (!open) return;
    const element = dialog.current;
    const trigger = toggle.current;
    element?.showModal();
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // In a preview frame (the editor's, or Preview's tablet and phone sizes)
    // the menu covers the frame's showing part, as it would that screen,
    // not the whole window.
    const frame = trigger?.closest<HTMLElement>("[data-site-frame]");
    const fit = () => {
      if (!element || !frame) return;
      const box = frame.getBoundingClientRect();
      const top = Math.max(box.top, 0);
      const bottom = Math.min(box.bottom, window.innerHeight);
      Object.assign(element.style, {
        top: `${top}px`,
        left: `${box.left}px`,
        width: `${box.width}px`,
        height: `${Math.max(bottom - top, 0)}px`,
        borderRadius: getComputedStyle(frame).borderRadius,
      });
    };
    fit();
    window.addEventListener("resize", fit);
    return () => {
      window.removeEventListener("resize", fit);
      element?.close();
      document.body.style.overflow = previous;
      trigger?.focus();
    };
  }, [open]);
  const navigate = (event: MouseEvent<HTMLAnchorElement>, page: EditorPage) => {
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    )
      return;
    event.preventDefault();
    setOpen(false);
    onNavigate(page);
  };
  const links = pages
    .filter((page) => page.is_visible)
    .map((page) => (
      <a
        key={page.id}
        className="focus-visible:outline-2 focus-visible:outline-site-accent focus-visible:outline-offset-4"
        href={href(page)}
        aria-current={page.id === currentPageId ? "page" : undefined}
        onClick={(event) => navigate(event, page)}
      >
        {page.title}
      </a>
    ));
  const brand = <Brand design={design} name={name} logoUrl={logoUrl} />;
  const [crowded, setHeader, setProbe] = useCrowded(links.length > 0);
  return (
    <header
      ref={setHeader}
      className="relative flex flex-wrap items-center justify-between gap-4 border-b border-site-border px-8 py-5 max-md:px-5 max-md:py-3 @max-[767px]/editor-preview:px-5 @max-[767px]/editor-preview:py-3"
    >
      {home && (
        <a
          className={siteNameClass}
          href={href(home)}
          onClick={(event) => navigate(event, home)}
        >
          {brand}
        </a>
      )}
      {links.length > 0 && (
        <>
          {/* The row as it would be on one line, measured to see if it fits
              (clipped, so it never widens the page). */}
          <div aria-hidden="true" className="pointer-events-none invisible absolute inset-0 overflow-hidden">
            <div ref={setProbe} className="flex w-max items-center gap-12 whitespace-nowrap">
              <span className="site-heading heading-size-20 leading-[1.4]">{brand}</span>
              <span className="flex gap-4 site-label-13">
                {pages.filter((page) => page.is_visible).map((page) => (
                  <span key={page.id} className="py-2">{page.title}</span>
                ))}
              </span>
            </div>
          </div>
          <button
            ref={toggle}
            className={`${crowded ? "flex" : "hidden max-md:flex @max-[767px]/editor-preview:flex"} size-11 items-center justify-center rounded-lg text-site-text focus-visible:outline-2 focus-visible:outline-site-accent focus-visible:outline-offset-4`}
            type="button"
            aria-label="Open site menu"
            aria-expanded={open}
            aria-controls={id}
            onClick={() => setOpen(true)}
          >
            <Menu size={22} />
          </button>
          <nav className={`${crowded ? "hidden" : "flex max-md:hidden @max-[767px]/editor-preview:hidden"} flex-wrap gap-4 site-label-13 text-site-muted [&_a]:py-2 [&_[aria-current=page]]:text-site-text [&_[aria-current=page]]:underline [&_[aria-current=page]]:underline-offset-6`} aria-label="Site pages">
            {links}
          </nav>
          {open &&
            createPortal(
              <dialog
                ref={dialog}
                id={id}
                {...siteDesignAttributes(design)}
                className="fixed inset-0 m-0 h-dvh max-h-none w-full max-w-none animate-[site-menu-in_220ms_ease-out] overflow-y-auto backdrop:bg-transparent overscroll-contain border-0 bg-site-bg p-6 font-site-body text-site-text motion-reduce:animate-none"
                aria-label="Site menu"
                onCancel={() => setOpen(false)}
              >
                <div className="flex items-center gap-6">
                  <span className={siteNameClass}>{brand}</span>
                  <button
                    type="button"
                    className="grid size-11 shrink-0 place-items-center focus-visible:outline-2 focus-visible:outline-site-accent focus-visible:outline-offset-4"
                    aria-label="Close site menu"
                    onClick={() => setOpen(false)}
                  >
                    <X size={26} />
                  </button>
                </div>
                <nav className="flex flex-col items-start gap-6 py-16 font-site-heading heading-size-32 leading-[1.3] font-medium [&_[aria-current=page]]:underline [&_[aria-current=page]]:underline-offset-8" aria-label="Site pages">{links}</nav>
              </dialog>,
              document.body,
            )}
        </>
      )}
    </header>
  );
}

/**
 * Whether the name (or logo) and the menu's links no longer fit on one line,
 * so the menu becomes its button at any width rather than wrapping. Measures
 * `probe`, the row laid out on one line, against `header`'s content box.
 */
function useCrowded(active: boolean) {
  const [header, setHeader] = useState<HTMLElement | null>(null);
  const [probe, setProbe] = useState<HTMLElement | null>(null);
  const [value, setValue] = useState(false);
  useLayoutEffect(() => {
    if (!active || !header || !probe) return;
    const check = () => {
      const style = getComputedStyle(header);
      const room = header.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      setValue(probe.getBoundingClientRect().width > room);
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(header);
    observer.observe(probe);
    // Web fonts and logos change the row's width once they load.
    void document.fonts?.ready.then(check);
    probe.querySelectorAll("img").forEach((image) => image.addEventListener("load", check));
    return () => {
      observer.disconnect();
      probe.querySelectorAll("img").forEach((image) => image.removeEventListener("load", check));
    };
  }, [active, header, probe]);
  return [active && value, setHeader, setProbe] as const;
}
